import { ApiResponse } from "@/types";
import { apiSlice } from "@/redux/apiSlice";
import { Consultation, ConsultationTable } from "@/types/consultation";
import consultationMockData from "@/mock/consultation-data.json";

export const ConsultationApi = apiSlice.injectEndpoints({
  endpoints(builder) {
    return {
      // Consultation Endpoints
      getConsultations: builder.query<
        ConsultationTable,
        { query: string; orgId: number }
      >({
        // TODO: Remove queryFn and use query once API is ready
        queryFn: async ({ query, orgId }) => {
          // Simulate API delay
          await new Promise((resolve) => setTimeout(resolve, 500));
          
          // Parse query string to extract parameters
          const urlParams = new URLSearchParams(query);
          const search_key = urlParams.get('search_key');
          const sort_key = urlParams.get('sort_key') || 'id';
          const sort_order = urlParams.get('sort_order') || 'desc';
          const limit = parseInt(urlParams.get('limit') || '10');
          const offset = parseInt(urlParams.get('offset') || '0');
          const status = urlParams.get('status');
          
          // Get all consultations from mock data
          let filteredConsultations = [...consultationMockData.data];
          
          // Filter by org_id
          filteredConsultations = filteredConsultations.filter(
            (consultation) => consultation.org_id === orgId
          );
          
          // Apply search filter (search in member_name and coach_name)
          if (search_key && search_key.trim() !== "") {
            const searchLower = search_key.toLowerCase();
            filteredConsultations = filteredConsultations.filter(
              (consultation) =>
                consultation.member_name?.toLowerCase().includes(searchLower) ||
                consultation.coach_name?.toLowerCase().includes(searchLower)
            );
          }
          
          // Apply status filter
          if (status && status.trim() !== "") {
            filteredConsultations = filteredConsultations.filter(
              (consultation) => consultation.status === status.toLowerCase()
            );
          }
          
          // Apply sorting
          filteredConsultations.sort((a, b) => {
            let aValue: any = a[sort_key as keyof typeof a];
            let bValue: any = b[sort_key as keyof typeof b];
            
            // Handle different data types
            if (typeof aValue === 'string' && typeof bValue === 'string') {
              aValue = aValue.toLowerCase();
              bValue = bValue.toLowerCase();
            }
            
            // Handle date strings
            if (sort_key === 'date' && typeof aValue === 'string' && typeof bValue === 'string') {
              aValue = new Date(aValue).getTime();
              bValue = new Date(bValue).getTime();
            }
            
            if (sort_order === 'asc') {
              return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else {
              return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            }
          });
          
          // Apply pagination
          const totalCount = filteredConsultations.length;
          const paginatedConsultations = filteredConsultations.slice(offset, offset + limit);
          
          return {
            data: {
              data: paginatedConsultations,
              total_counts: consultationMockData.total_counts,
              filtered_counts: totalCount,
            } as ConsultationTable,
          };
        },
        // query: ({ query, orgId }) => ({
        //   url: `/consultation?org_id=${orgId}${query ? `&${query}` : ""}`,
        //   method: "GET",
        //   headers: {
        //     Accept: "application/json",
        //   },
        // }),
        providesTags: (result, error, { orgId }) => [
          { type: "Consultation", id: `LIST_${orgId}` },
          "Consultation",
        ],
      }),

      getConsultationById: builder.query<
        Consultation,
        {
          org_id: number;
          id: number;
          query?: string;
        }
      >({
        query: ({ id, org_id, query }) => ({
          url: `/consultation/${id}?org_id=${org_id}${
            query && query.length > 0 ? `&${query}` : ""
          }`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: (result, error, { id, org_id }) => [
          { type: "Consultation", id: `${org_id}_${id}` },
          "Consultation",
        ],
        // Immediately drop cache when there are no active subscribers
        keepUnusedDataFor: 0,
      }),

      createConsultation: builder.mutation<ApiResponse, Consultation>({
        query: (consultation) => ({
          url: `/consultation`,
          method: "POST",
          body: consultation,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Consultation"],
      }),

      updateConsultation: builder.mutation<ApiResponse, Consultation>({
        // TODO: Remove queryFn and use query once API is ready
        queryFn: async (consultation) => {
          // Simulate API delay
          await new Promise((resolve) => setTimeout(resolve, 300));
          return {
            data: {
              status_code: 200,
              id: consultation.id || 0,
              message: "Consultation updated successfully",
            } as ApiResponse,
          };
        },
        // query: (consultation) => ({
        //   url: `/consultation/${consultation.id}`,
        //   method: "PUT",
        //   body: consultation,
        //   headers: {
        //     Accept: "application/json",
        //   },
        // }),
        invalidatesTags: ["Consultation"],
      }),

      deleteConsultation: builder.mutation<ApiResponse, number>({
        // TODO: Remove queryFn and use query once API is ready
        queryFn: async (consultation_id) => {
          // Simulate API delay
          await new Promise((resolve) => setTimeout(resolve, 300));
          return {
            data: {
              status_code: 200,
              id: consultation_id,
              message: "Consultation deleted successfully",
            } as ApiResponse,
          };
        },
        // query: (consultation_id) => ({
        //   url: `/consultation/${consultation_id}`,
        //   method: "DELETE",
        //   headers: {
        //     Accept: "application/json",
        //   },
        // }),
        invalidatesTags: ["Consultation"],
      }),
    };
  },
});

export const {
  useGetConsultationsQuery,
  useGetConsultationByIdQuery,
  useCreateConsultationMutation,
  useUpdateConsultationMutation,
  useDeleteConsultationMutation,
} = ConsultationApi;

